/**
 * Script Sanitizer — Validates and safely injects user-provided scripts
 * Prevents XSS, eval attacks, and site-crashing code
 */

// Dangerous patterns that could crash/hijack the site
const BLOCKED_PATTERNS = [
  /document\.write/gi,
  /document\.writeln/gi,
  /eval\s*\(/gi,
  /Function\s*\(/gi,
  /setTimeout\s*\(\s*["'`]/gi,
  /setInterval\s*\(\s*["'`]/gi,
  /window\.location\s*=/gi,
  /document\.location\s*=/gi,
  /window\.open\s*\(/gi,
  /\.innerHTML\s*=/gi,
  /\.outerHTML\s*=/gi,
  /on\w+\s*=\s*["']/gi, // inline event handlers
  /javascript\s*:/gi,
  /data\s*:\s*text\/html/gi,
  /import\s*\(/gi, // dynamic imports
  /crypto\.subtle/gi,
  /XMLHttpRequest/gi,
  /fetch\s*\(\s*["'`](?!https?:\/\/(www\.)?(google|facebook|analytics|hotjar|clarity|gtag|googletagmanager|connect\.facebook))/gi,
];

// Only allow known trusted script domains
const TRUSTED_DOMAINS = [
  'google-analytics.com',
  'googletagmanager.com',
  'google.com',
  'googleapis.com',
  'gstatic.com',
  'facebook.net',
  'facebook.com',
  'connect.facebook.net',
  'hotjar.com',
  'clarity.ms',
  'cloudflare.com',
  'cloudflareinsights.com',
  'tawk.to',
  'crisp.chat',
  'intercom.io',
  'hubspot.com',
  'hs-scripts.com',
  'hs-analytics.net',
  'linkedin.com',
  'snap.licdn.com',
  'twitter.com',
  'platform.twitter.com',
  'ads-twitter.com',
  'pinterest.com',
  'pinimg.com',
  'tiktok.com',
  'analytics.tiktok.com',
  'schema.org',
  'jsdelivr.net',
  'cdnjs.cloudflare.com',
  'unpkg.com',
];

export interface SanitizeResult {
  safe: boolean;
  warnings: string[];
  sanitizedCode: string;
}

/** Validate a tracking ID format */
export function validateTrackingId(type: string, value: string): { valid: boolean; message: string } {
  const trimmed = value.trim();
  if (!trimmed) return { valid: true, message: '' };

  const patterns: Record<string, { regex: RegExp; example: string }> = {
    google_analytics: { regex: /^G-[A-Z0-9]{6,12}$/i, example: 'G-XXXXXXXXXX' },
    google_tag_manager: { regex: /^GTM-[A-Z0-9]{6,8}$/i, example: 'GTM-XXXXXXX' },
    meta_pixel: { regex: /^\d{10,20}$/, example: '123456789012345' },
    hotjar: { regex: /^\d{5,10}$/, example: '1234567' },
    google_search_console: { regex: /^[a-zA-Z0-9_-]{20,60}$/, example: 'verification code' },
  };

  const pattern = patterns[type];
  if (!pattern) return { valid: true, message: '' };

  if (!pattern.regex.test(trimmed)) {
    return { valid: false, message: `Invalid format. Expected: ${pattern.example}` };
  }
  return { valid: true, message: '' };
}

/** Sanitize custom script code */
export function sanitizeScript(code: string): SanitizeResult {
  const warnings: string[] = [];

  if (!code.trim()) return { safe: true, warnings: [], sanitizedCode: '' };

  // Check for blocked patterns
  for (const pattern of BLOCKED_PATTERNS) {
    if (pattern.test(code)) {
      warnings.push(`Blocked dangerous pattern: ${pattern.source}`);
      pattern.lastIndex = 0; // Reset regex
    }
  }

  if (warnings.length > 0) {
    return { safe: false, warnings, sanitizedCode: '' };
  }

  // Validate script src domains
  const srcMatches = code.matchAll(/src\s*=\s*["']([^"']+)["']/gi);
  for (const match of srcMatches) {
    const url = match[1];
    try {
      const hostname = new URL(url).hostname;
      const isTrusted = TRUSTED_DOMAINS.some(d => hostname === d || hostname.endsWith(`.${d}`));
      if (!isTrusted) {
        warnings.push(`Script source "${hostname}" is not in the trusted domains list. Proceed with caution.`);
      }
    } catch {
      warnings.push(`Invalid URL in script src: "${url}"`);
    }
  }

  return {
    safe: warnings.length === 0,
    warnings,
    sanitizedCode: code.trim(),
  };
}

/** Remove all previously injected integration elements */
function clearInjectedScripts() {
  const ids = [
    'ga-script', 'ga-config', 'gtm-script', 'meta-pixel', 'hotjar-script',
    'gsc-verification', 'custom-head-container', 'custom-body-container'
  ];
  ids.forEach(id => document.getElementById(id)?.remove());
}

/** Safely inject integration scripts into the page */
export function injectIntegrationScripts(forceReload = false): void {
  try {
    if (forceReload) {
      clearInjectedScripts();
    }

    const stored = localStorage.getItem('site_integrations');
    if (!stored) {
      console.log('[Integrations] No integrations found in localStorage');
      return;
    }

    const integrations = JSON.parse(stored);
    console.log('[Integrations] Loading integrations:', JSON.stringify(Object.keys(integrations).filter(k => integrations[k]?.enabled), null, 2));

    // Google Analytics — skip if already injected by early boot script
    if (integrations.google_analytics?.enabled && integrations.google_analytics.trackingId) {
      const id = integrations.google_analytics.trackingId.trim();
      if (/^G-[A-Z0-9]+$/i.test(id)) {
        if (!document.getElementById('ga-script')) {
          console.log('[Integrations] Injecting Google Analytics:', id);
          injectInlineScript(`window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','${id}',{send_page_view:true});`, 'ga-config');
          injectScriptTag(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`, 'ga-script');
        } else {
          console.log('[Integrations] GA4 already loaded by early injection, skipping');
        }
      } else {
        console.warn('[Integrations] Invalid GA4 ID format:', id);
      }
    }

    // Google Tag Manager — skip if already injected
    if (integrations.google_tag_manager?.enabled && integrations.google_tag_manager.trackingId) {
      const id = integrations.google_tag_manager.trackingId.trim();
      if (/^GTM-[A-Z0-9]+$/i.test(id)) {
        if (!document.getElementById('gtm-script')) {
          console.log('[Integrations] Injecting GTM:', id);
          injectInlineScript(`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;j.id='gtm-script';f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');`, 'gtm-inline');
        } else {
          console.log('[Integrations] GTM already loaded by early injection, skipping');
        }
      } else {
        console.warn('[Integrations] Invalid GTM ID format:', id);
      }
    }

    // Meta Pixel — skip if already injected
    if (integrations.meta_pixel?.enabled && integrations.meta_pixel.trackingId) {
      const id = integrations.meta_pixel.trackingId.trim();
      if (/^\d+$/.test(id)) {
        if (typeof (window as any).fbq !== 'function') {
          console.log('[Integrations] Injecting Meta Pixel:', id);
          injectInlineScript(`!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${id}');fbq('track','PageView');`, 'meta-pixel');
        } else {
          console.log('[Integrations] Meta Pixel already loaded by early injection, skipping');
        }
      } else {
        console.warn('[Integrations] Invalid Meta Pixel ID format:', id);
      }
    }

    // Hotjar — skip if already injected
    if (integrations.hotjar?.enabled && integrations.hotjar.trackingId) {
      const id = integrations.hotjar.trackingId.trim();
      if (/^\d+$/.test(id)) {
        if (!document.getElementById('hotjar-script')) {
          console.log('[Integrations] Injecting Hotjar:', id);
          injectInlineScript(`(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:${id},hjsv:6};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.id='hotjar-script';r.src=t+h._hjSettings.hjid+j;a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=6');`, 'hotjar-inline');
        } else {
          console.log('[Integrations] Hotjar already loaded by early injection, skipping');
        }
      }
    }

    // Google Search Console — skip if already injected
    if (integrations.google_search_console?.enabled && integrations.google_search_console.verificationCode) {
      if (!document.getElementById('gsc-verification')) {
        let code = integrations.google_search_console.verificationCode.trim();
        const contentMatch = code.match(/content\s*=\s*["']([^"']+)["']/i);
        if (contentMatch) code = contentMatch[1];
        if (code.includes('=')) code = code.split('=').pop()?.trim() || code;
        
        if (code.length > 0) {
          console.log('[Integrations] Injecting GSC verification meta tag');
          const meta = document.createElement('meta');
          meta.name = 'google-site-verification';
          meta.content = code;
          meta.id = 'gsc-verification';
          document.head.appendChild(meta);
        }
      } else {
        console.log('[Integrations] GSC already loaded by early injection, skipping');
      }
    }

    // Custom head/body scripts — sanitized
    if (integrations.custom_head_scripts?.enabled && integrations.custom_head_scripts.code) {
      const result = sanitizeScript(integrations.custom_head_scripts.code);
      if (result.safe) {
        safeInjectHTML(integrations.custom_head_scripts.code, 'head', 'custom-head');
      }
    }

    if (integrations.custom_body_scripts?.enabled && integrations.custom_body_scripts.code) {
      const result = sanitizeScript(integrations.custom_body_scripts.code);
      if (result.safe) {
        safeInjectHTML(integrations.custom_body_scripts.code, 'body', 'custom-body');
      }
    }
    console.log('[Integrations] Script injection complete. Check <head> for injected elements.');
  } catch (error) {
    console.error('[Integrations] Failed to inject scripts:', error);
  }
}

/** Track SPA page navigation for all active integrations */
export function trackPageView(url: string): void {
  try {
    const stored = localStorage.getItem('site_integrations');
    if (!stored) return;
    const integrations = JSON.parse(stored);

    // GA4 page view
    if (integrations.google_analytics?.enabled && integrations.google_analytics.trackingId) {
      if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('config', integrations.google_analytics.trackingId.trim(), {
          page_path: url,
        });
      }
    }

    // GTM virtual page view
    if (integrations.google_tag_manager?.enabled) {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'virtualPageview',
        page: url,
      });
    }

    // Meta Pixel page view
    if (integrations.meta_pixel?.enabled) {
      if (typeof (window as any).fbq === 'function') {
        (window as any).fbq('track', 'PageView');
      }
    }
  } catch {
    // silently ignore
  }
}

function injectScriptTag(src: string, id: string) {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  script.src = src;
  script.onload = () => console.log(`[Integrations] External script loaded: ${id}`);
  script.onerror = () => console.error(`[Integrations] Failed to load external script: ${id} (${src})`);
  document.head.appendChild(script);
}

function injectInlineScript(code: string, id: string) {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.textContent = code;
  document.head.appendChild(script);
  console.log(`[Integrations] Inline script injected: ${id}`);
}

function safeInjectHTML(html: string, target: 'head' | 'body', id: string) {
  if (document.getElementById(`${id}-container`)) return;
  const container = document.createElement('div');
  container.id = `${id}-container`;
  container.style.display = 'none';

  // Parse safely — only allow script, meta, link, noscript, style tags
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const allowedTags = ['SCRIPT', 'META', 'LINK', 'NOSCRIPT', 'STYLE'];

  const elements = doc.head.children.length > 0 ? doc.head.children : doc.body.children;
  Array.from(elements).forEach((el) => {
    if (allowedTags.includes(el.tagName)) {
      const clone = document.createElement(el.tagName.toLowerCase());
      Array.from(el.attributes).forEach((attr) => {
        clone.setAttribute(attr.name, attr.value);
      });
      if (el.textContent) clone.textContent = el.textContent;
      (target === 'head' ? document.head : document.body).appendChild(clone);
    }
  });
}
